'use client';

import Slider from 'react-slick';
import { FC, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Divider, Typography } from '@mui/material';

import { useGetMerchants } from 'src/api/shop';

import Iconify from '../../../../components/iconify';
// eslint-disable-next-line import/no-cycle
import { IMerchant } from '../../../../types/merchant';
import { ComponentTypeProps } from '../../../../types/page-generator';
import { AlternativeMerchantCarousel } from './merchants/alternative-merchant-carousel';
import { useResponsive } from '../../../../hooks/use-responsive';
import { useRouter } from '../../../../routes/hooks';
import { useLocales } from '../../../../locales';

export const MerchantsCarousel: FC<ComponentTypeProps> = ({ block }) => {
  const { currentLang } = useLocales();
  const isMobile = useResponsive('down', 'sm');
  const actionButtons: any = block?.actionButtons || [];
  const { merchants } = useGetMerchants({}) || [];
  let sliderRef: Slider | any;
  const text: string = block?.text as string;
  const size = block?.settings?.enableDouble ? 5 : 8;
  const [filteredMerchants, setFilteredMerchants] = useState<IMerchant[]>([]);
  const router = useRouter();

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_BOT_ID === '506') {
      const array = merchants.filter((item: IMerchant) => item.title !== 'test_aleks');
      setFilteredMerchants(array);
    }
    if (process.env.NEXT_PUBLIC_BOT_ID === '504') {
      const ar = merchants.filter((item: IMerchant) => item.title !== 'B24club');
      setFilteredMerchants(ar);
    }
  }, [merchants]);

  const CustomPrevArrow = ({ onClick }: any) => (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: 'transparent',
        position: 'absolute',
        top: '50%',
        right: '-40px',
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        zIndex: 100,
      }}
    >
      <Iconify
        icon={currentLang.value === 'he' ? 'ep:arrow-left-bold' : 'ep:arrow-right-bold'}
        color="rgba(14, 23, 50, 1)"
      />
    </Box>
  );

  const CustomNextArrow = ({ onClick }: any) => (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: 'transparent',
        position: 'absolute',
        top: '50%',
        left: '-40px',
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        zIndex: 100,
      }}
    >
      <Iconify
        icon={currentLang.value === 'he' ? 'ep:arrow-right-bold' : 'ep:arrow-left-bold'}
        color="rgba(14, 23, 50, 1)"
      />
    </Box>
  );

  const handlePrevClick = () => {
    sliderRef.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.slickNext();
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: !block?.settings?.enablePriceOnTop ? size : 6,
    slidesToScroll: 1,
    arrows: !isMobile,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
    nextArrow: <CustomPrevArrow onClick={handlePrevClick} />,
    prevArrow: <CustomNextArrow onClick={handleNextClick} />,
  };

  return (
    <Box
      sx={{
        '& .slick-track': { mt: '10px' },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>{text}</Typography>
        {actionButtons.length > 0 && (
          <Stack direction="row" alignItems="center">
            <Typography
              sx={{ mr: 1, cursor: 'pointer' }}
              onClick={() => router.push(`${actionButtons[0].link}`)}
            >
              {actionButtons[0]?.label}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />

      <Slider {...settings}>
        {filteredMerchants?.map((merchant: IMerchant, id: number) => (
          <Box key={id} sx={{ px: 0.5 }}>
            <AlternativeMerchantCarousel block={block} merchant={merchant} />
          </Box>
        ))}
      </Slider>
    </Box>
  );
};
